import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { requestHandler } from 'lib/api';
import { Dispatch } from 'redux';

import {
  ApprovalStatus,
  ApprovalType,
  ComiteeType,
  GetMissingApprovalsResponse,
} from './types';

export const getMissingApprovals = (
  dispatch: Dispatch,
  requestType: ApprovalType,
  requestId: number,
  comiteeType: ComiteeType,
) =>
  useQuery({
    queryKey: ['approvals', 'missing', requestType, requestId, comiteeType],
    queryFn: async (): Promise<GetMissingApprovalsResponse | undefined> => {
      const response = await requestHandler({
        method: 'get',
        url: `internal/approvals/missing/${requestType}/${requestId}/${comiteeType}`,
        dispatch,
      });
      if (response) return response.data;
    },
    retry: false,
  });

export const submitApproval = (
  dispatch: Dispatch,
  requestType: ApprovalType,
  requestId: number,
  comiteeType: ComiteeType,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['approvals', 'submit', requestType, requestId, comiteeType],
    mutationFn: async (body: {
      decision: ApprovalStatus;
      roleId: number;
      message: string;
    }): Promise<string> => {
      const response = await requestHandler({
        method: 'post',
        url: `internal/approvals/submit/${requestType}/${requestId}/${comiteeType}`,
        data: body,
        dispatch,
      });
      if (response) return response.data;
      return 'OK';
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({
        queryKey: ['approvals', 'missing', requestType, requestId, comiteeType],
      });
    },
  });
};

export const deleteApproval = (
  dispatch: Dispatch,
  requestType: ApprovalType,
  requestId: number,
  comiteeType: ComiteeType,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['approvals', 'delete'],
    mutationFn: async (id: number): Promise<string> => {
      const response = await requestHandler({
        method: 'delete',
        url: `internal/approvals/${id}`,
        dispatch,
      });
      if (response) return response.data;
      return 'OK';
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({
        queryKey: ['approvals', 'missing', requestType, requestId, comiteeType],
      });
    },
  });
};

export const updateRequestComitee = (
  dispatch: Dispatch,
  requestType: ApprovalType,
  requestId: number,
  comiteeType: ComiteeType,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [
      'approvals',
      'updateComitee',
      requestType,
      requestId,
      comiteeType,
    ],
    mutationFn: async (id: number): Promise<string> => {
      const response = await requestHandler({
        method: 'patch',
        url: `internal/approvals/updateComitee/${requestType}/${requestId}/${comiteeType}`,
        data: {
          id,
        },
        dispatch,
      });
      if (response) return response.data;
      return 'OK';
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({
        queryKey: ['approvals', 'missing', requestType, requestId, comiteeType],
      });
    },
  });
};
